import { put, call, takeEvery } from 'redux-saga/effects'
import {
  FetchTeamRoles,
  FetchTeamPermissions,
  CreateTeamRole,
  DeleteTeamRole,
} from 'src/constants/actionTypes'
import { callSecureApi, ApiService } from 'src/sagas/api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import { AsyncAction } from 'src/utils/reduxUtils'
import { TeamPermissions, TeamRole } from 'src/interfaces/team'

function* onFetchTeamRoles(action: AsyncAction<null, { data: TeamRole[] }>) {
  try {
    const { payload }: { payload: { data: TeamRole[] } } = yield call(
      callSecureApi,
      Endpoints.FetchTeamRoles,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchTeamRoles.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch team roles'
    yield put(FetchTeamRoles.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchTeamPermissions(action: AsyncAction<null, TeamPermissions>) {
  try {
    const { payload }: { payload: TeamPermissions } = yield call(
      callSecureApi,
      Endpoints.FetchTeamPermissions,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchTeamPermissions.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch team permissions'
    yield put(FetchTeamPermissions.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface CreateTeamRoleRequest {
  teamRoleName: string
  permissions: string[]
}

function* onCreateTeamRole(action: AsyncAction<CreateTeamRoleRequest>) {
  const { permissions, teamRoleName } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.CreateTeamRole,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body: { name: teamRoleName, permissions },
      },
    )

    yield put(CreateTeamRole.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to create team role'

    yield put(CreateTeamRole.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface DeleteTeamRoleRequest {
  teamRoleId: string
}

function* onDeleteTeamRole(action: AsyncAction<DeleteTeamRoleRequest>) {
  const { teamRoleId } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.DeleteTeamRole,
      {
        method: 'DELETE',
        apiService: ApiService.Paraworks,
        params: [{ field: ':teamRoleId', value: teamRoleId }],
      },
    )

    yield put(DeleteTeamRole.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to delete team role'

    yield put(DeleteTeamRole.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* teamRolesSaga() {
  yield takeEvery(FetchTeamRoles.type.REQUEST, onFetchTeamRoles)
  yield takeEvery(FetchTeamPermissions.type.REQUEST, onFetchTeamPermissions)
  yield takeEvery(CreateTeamRole.type.REQUEST, onCreateTeamRole)
  yield takeEvery(DeleteTeamRole.type.REQUEST, onDeleteTeamRole)
}
